/* eslint-disable react-hooks/rules-of-hooks */
import { AttributeIdentifiers } from "@lyfegen-platform/graphql-schema/models";
import { distinctBy, getTreatmentCodeByCountry, isEmptyValue, isFixedPrice } from "@lyfegen-platform/utils";
import { Box, InputAdornment, Typography } from "@material-ui/core";
import { useField, useFieldValue } from "formik";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";
import * as Yup from "yup";
import { useDiscountRate } from "../hooks/use-discount-rate";
import { ControlState } from "../models/flexible/control-state";
import { FlexControlProps } from "../models/flexible/flex-field-controller";
import { combinePaths, mapBasePriceKeys } from "../models/flexible/utils";
import { PriceCategory, PriceType, PublicPrice, Treatment } from "../models/generated/graphql";
import { flexibleFormRelatedAgreements } from "../store/flexible";
import formatCurrency from "../utils/currency-format";
import { firstLetterUppercase } from "../utils/first-letter-uppercase";
import { getPathPrefix } from "../utils/get-path-prefix";
import { maxBy } from "../utils/max-by";
import { renderRequiredMessage } from "../utils/render-required-message";
import { GeneralFieldController } from "./general-field-controller";

const BasePriceTypeInformation = (props: { path: string }) => {
	const [{ value: packReference }] = useField<Treatment>(combinePaths(getPathPrefix(props.path, 1), AttributeIdentifiers.PackReference));
	const [{ value: priceType }] = useField<PriceType>(combinePaths(getPathPrefix(props.path, 1), AttributeIdentifiers.BasePrice));
	const agreementsData = useRecoilValue(flexibleFormRelatedAgreements);
	const { t } = useTranslation();
	const treatment = distinctBy(
		agreementsData?.data?.flatMap(({ agreement }) => agreement.treatmentPricesConnection.edges).map((edge) => edge.node) || [],
		(treatment) => getTreatmentCodeByCountry(treatment) || ""
	).find((t) => getTreatmentCodeByCountry(t) === getTreatmentCodeByCountry(packReference));
	const prices =
		priceType === PriceType.PublicPricePercent
			? treatment?.prices.filter((price) => price.priceType === PriceCategory.Ppub)
			: treatment?.prices.filter((price) => price.priceType === PriceCategory.Pexf);
	const latestPrice = maxBy((prices || []) as Array<PublicPrice>, (t) => t.ValidFromDate);
	const latestAgreement = maxBy(agreementsData?.data || [], ({ agreement }) => agreement.pushedOn)?.agreement;

	if (!priceType) {
		return <></>;
	}

	return (
		<Box paddingY={2.5}>
			<Typography variant="caption" color="textSecondary">
				{firstLetterUppercase(t(mapBasePriceKeys(priceType)))}
			</Typography>
			<Typography>
				{latestPrice ? `${formatCurrency(latestPrice?.Price, 2, 2)} ${latestAgreement?.currency?.Code || ""}` : "n/a"}
			</Typography>
		</Box>
	);
};

export class SuggestedDiscountRateFieldController extends GeneralFieldController {
	override createValidationRule(_initialData: unknown, translation: (key: string) => string): Yup.AnySchema {
		return Yup.number()
			.nullable()
			.test("is-valid-rate", translation("between-0-and-100"), (rate?: any) => isEmptyValue(rate) || (rate >= 0 && rate <= 100))
			.when(AttributeIdentifiers.BasePrice, (basePrice, schema) =>
				basePrice === PriceType.CustomPricePercent &&
				basePrice === PriceType.PublicPricePercent &&
				basePrice === PriceType.ExFactoryPricePercent
					? schema.required(renderRequiredMessage(this.schema.attribute.identifier, translation))
					: schema
			);
	}

	override useControlState(path: string): ControlState {
		const [{ value }] = useField(this.parent.path);
		const index = +(path.split(".").find((e) => Number.isInteger(+e)) || 0);
		const basePriceValue = value[index][AttributeIdentifiers.BasePrice];
		const [decisionTherapy] = useFieldValue(AttributeIdentifiers.DecisionTherapy);

		return decisionTherapy == null || isEmptyValue(basePriceValue) || isFixedPrice(basePriceValue) ? "hidden" : "enabled";
	}

	override useControlProps(path: string): FlexControlProps {
		const splittedPath = this.path.split(".");
		const index = +(path.split(".").find((e) => Number.isInteger(+e)) || 0);
		const setDiscountRate = useDiscountRate({
			index: index,
			sectionPath: splittedPath[splittedPath.length - 2],
			fieldPath: splittedPath[splittedPath.length - 1],
			childIndexes: this.parent.context.childBrandIndexes?.[index] || [],
		});

		return {
			InputLabelProps: {
				shrink: true,
			},
			InputProps: {
				endAdornment: <InputAdornment position="end">%</InputAdornment>,
				inputProps: {
					max: 100,
					onBlur: () => {
						if (this.parent.context.uniqueBrandIndexes?.includes(index)) {
							setDiscountRate();
						}
					},
				},
			},
		};
	}

	renderAfter(path: string) {
		return <BasePriceTypeInformation path={path} />;
	}
}
